/**
 * Required metadata keys per event (C-AN-18). Mirrors `event_required_metadata`
 * in the database; `eventPayloadContracts.test.ts` fails if they drift.
 * Adding a contract here makes every emitter that omits a key a compile error.
 */
export const EVENT_REQUIRED_KEYS = {
  ai_prompt_sent: ['feature', 'surface', 'prompt_length'],
  coa_processed: ['listing_id'],
  coa_uploaded: ['listing_id'],
  contact_link_clicked: ['method', 'source', 'target_hash'],
  desk_graduated: ['persona', 'rep_version', 'days_since_start'],
  desk_step_completed: ['rep_key', 'persona', 'rep_version', 'days_since_start'],
  document_opened: ['listing_id', 'action'],
  filter_applied: ['surface', 'filter_key'],
  image_viewed: ['surface', 'media_count'],
  info_requested: ['listing_id'],
  inquiry_sent: ['listing_id', 'type'],
  listing_copied: ['menu_id', 'listing_count', 'copied_count', 'skipped_count'],
  listing_created: ['listing_id'],
  listings_bulk_updated: ['listing_count', 'fields'],
  listing_updated: ['listing_id'],
  listing_view: ['listing_id', 'menu_id'],
  menu_created: ['menu_id'],
  menu_shared: ['menu_id', 'share_id'],
  menu_view: ['menu_id'],
  next_move_clicked: ['rule_key', 'persona', 'priority', 'surface'],
  next_move_completed: ['rule_key', 'persona', 'priority', 'surface'],
  next_move_dismissed: ['rule_key', 'persona', 'priority', 'surface'],
  next_move_shown: ['rule_key', 'persona', 'priority', 'surface'],
  next_move_skipped: ['rule_key', 'persona', 'priority', 'surface'],
  search_performed: ['surface', 'query_length', 'term_count', 'result_count', 'zero_results'],
  share_redeemed: ['share_token'],
} as const;

type Contracts = typeof EVENT_REQUIRED_KEYS;
export type ContractedEvent = keyof Contracts;

/** Payload type for an event: required keys (non-null) plus any extras. */
export type EventPayload<E extends string> = E extends ContractedEvent
  ? { [K in Contracts[E][number]]: string | number | boolean | string[] | Record<string, unknown> } & Record<string, unknown>
  : Record<string, unknown> | undefined;

/** Runtime guard: keys missing or null for a contracted event. */
export function missingRequiredKeys(eventType: string, metadata: Record<string, unknown> | undefined): string[] {
  const keys = (EVENT_REQUIRED_KEYS as Record<string, readonly string[]>)[eventType];
  if (!keys) return [];
  return keys.filter((k) => metadata?.[k] === undefined || metadata?.[k] === null);
}
